import { type AppId, appIdToAppInfoMap } from '@post-office/rovo-expansion-common';

import {
	FULL_CONNECTOR_SOURCE,
	SMARTLINKS_CONNECTOR_SOURCE,
} from '@atlassian/rovo-connectors-config/schemas/types';

import { classifyConnectorRow } from './classifyConnectorRow.ts';
import { resolveAppIdForConnectorName } from './connectorTypeNames.ts';
import { type ConnectorRow, type QualifyingProduct } from '../thirdPartyPromptTypes.ts';

export type ProviderConnectorGrants = {
	providerId: string;
	appId: AppId;
	smartLinkGranted: boolean;
	fullConnectorGranted: boolean;
	/** First non-empty address across the full connector rows. */
	fullConnectorAuthUrl?: string;
};

type QualifyingGrants = ProviderConnectorGrants & { fullConnectorAuthUrl: string };

type Draft = {
	appId?: AppId;
	smartLinkGranted: boolean;
	hasFullConnector: boolean;
	fullConnectorGranted: boolean;
	fullConnectorAuthUrl?: string;
};

/** `providerId` is the whole join: `containerId` plus `serviceKey` matches across products. */
export const pairConnectorRowsByProviderId = (rows: ConnectorRow[]): ProviderConnectorGrants[] => {
	const drafts: Map<string, Draft> = new Map();

	for (const row of rows) {
		const { providerId } = row;
		if (!providerId) {
			continue;
		}

		let draft = drafts.get(providerId);
		if (!draft) {
			draft = { smartLinkGranted: false, hasFullConnector: false, fullConnectorGranted: false };
			drafts.set(providerId, draft);
		}

		const source = classifyConnectorRow(row);

		if (source === FULL_CONNECTOR_SOURCE) {
			draft.hasFullConnector = true;
			draft.fullConnectorGranted = draft.fullConnectorGranted || row.isUserOAuthed;
			draft.fullConnectorAuthUrl = draft.fullConnectorAuthUrl || row.outboundAuthUrl || undefined;
			continue;
		}

		// An unmappable Smart Links row must not hide a sibling that names a product.
		if (source === SMARTLINKS_CONNECTOR_SOURCE && !draft.appId) {
			const appId = resolveAppIdForConnectorName(row.name);

			if (appId) {
				draft.appId = appId;
				draft.smartLinkGranted = row.isUserOAuthed;
			}
		}
	}

	const pairs: ProviderConnectorGrants[] = [];

	for (const [providerId, draft] of drafts) {
		const { appId, hasFullConnector } = draft;

		if (appId && hasFullConnector) {
			pairs.push({
				providerId,
				appId,
				smartLinkGranted: draft.smartLinkGranted,
				fullConnectorGranted: draft.fullConnectorGranted,
				fullConnectorAuthUrl: draft.fullConnectorAuthUrl,
			});
		}
	}

	return pairs;
};

/** The caller adds the last part of the rule, that the page links to the product. */
export const canPromptToConnect = (grants: ProviderConnectorGrants): grants is QualifyingGrants =>
	grants.smartLinkGranted && !grants.fullConnectorGranted && Boolean(grants.fullConnectorAuthUrl);

export const selectQualifyingProducts = (rows: ConnectorRow[]): QualifyingProduct[] => {
	const seen: Set<AppId> = new Set();
	const products: QualifyingProduct[] = [];

	for (const grants of pairConnectorRowsByProviderId(rows)) {
		if (!canPromptToConnect(grants) || seen.has(grants.appId)) {
			continue;
		}

		seen.add(grants.appId);

		const appInfo = appIdToAppInfoMap[grants.appId];

		products.push({
			appId: grants.appId,
			displayName: appInfo.normalizedAppName,
			iconUrl: appInfo.smartlinkLogo ?? appInfo.logo,
			outboundAuthUrl: grants.fullConnectorAuthUrl,
		});
	}

	return products;
};
